// CreaGruppo.tsx — «Partita a 4» (v013): per chi ha Gold. Il server crea il gruppo e il link /g/<codice>; chi lo crea ha
// il primo posto (dà il via) e manda il link agli altri tre (anche senza account).
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CampoNome } from "../gioco/CampoNome.tsx";
import { creaGruppo, salvaTokenGruppo } from "../servizi/gruppi.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { vai } from "../servizi/percorso.ts";
import { segnaSpiegazione, spiegazioneVista } from "../servizi/spiegazioni.ts";
import { useAccount } from "../servizi/account.ts";
import { ErroreRete } from "../servizi/api.ts";
import { PERCORSO_ACCESSO, PERCORSO_GRUPPO, PERCORSO_PIANI, PREFERENZE } from "../costanti.ts";

const ERRORI_NOTI = ["gruppi_spenti", "serve_gold", "troppi_gruppi", "tempo_gruppi_finito", "gruppo_pieno", "gruppo_iniziato", "non_trovato", "non_autenticato", "offline", "timeout", "troppe_richieste", "bloccato", "nome_vietato"];
export const codiceErroreGruppo = (e: unknown): string => {
  const c = e instanceof ErroreRete ? e.codice : "generico";
  return ERRORI_NOTI.includes(c) ? c : "generico";
};

export function CreaGruppo({ onAnnulla }: { onAnnulla: () => void }) {
  const { t } = useTranslation();
  const account = useAccount();
  const [nome, setNome] = useState(() => (account.tipo === "registrato" ? account.io.nome : leggiPreferenza(PREFERENZE.nome)) ?? "");
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const nomeAccount = account.tipo === "registrato" ? account.io.nome : null;
  useEffect(() => {
    if (nomeAccount) setNome((n) => n || nomeAccount);
  }, [nomeAccount]);

  // v039: una creazione sola (prima il clic su «Crea» segnava la spiegazione vista e la creazione «diretta» ne faceva
  // una seconda: due partite, e si finiva nell'ultima)
  const diretta = useRef(false);
  const crea = (n: string) => {
    diretta.current = true;
    salvaPreferenza(PREFERENZE.nome, n);
    segnaSpiegazione("gruppo");
    setInvio(true);
    setErrore(null);
    creaGruppo(n)
      .then((r) => {
        salvaTokenGruppo(r.codice, r.token);
        vai(`${PERCORSO_GRUPPO}${r.codice}`);
      })
      .catch((err: unknown) => {
        setErrore(codiceErroreGruppo(err));
        setInvio(false);
      });
  };
  // v038: spiegazione già vista e nome noto → si crea subito, senza finestra da riempire
  const pianoOk = account.tipo === "registrato" && account.io.piano === "gold";
  const subito = pianoOk && spiegazioneVista("gruppo") && !!nome.trim() && !errore;
  useEffect(() => {
    if (!subito || diretta.current) return;
    diretta.current = true;
    crea(nome.trim());
  }, [subito]); // eslint-disable-line react-hooks/exhaustive-deps
  if (subito)
    return (
      <div className="crea-sfida" role="status" aria-busy="true">
        <p className="nota-centrata">{t("gioco.creazione")}</p>
      </div>
    );

  if (account.tipo === "caricamento") return <p className="nota-centrata" role="status">{t("generale.caricamento")}</p>;
  if (account.tipo !== "registrato" || account.io.piano !== "gold") {
    const ospite = account.tipo !== "registrato";
    return (
      <div className="crea-sfida">
        <p className="nota">{t("gruppo.come_funziona")}</p>
        <p className="pannello-testo">{t(ospite ? "gruppo.serve_account" : "gruppo.serve_gold")}</p>
        <div className="azioni-riga">
          <button type="button" className="pulsante pulsante-secondario" onClick={onAnnulla}>
            {t("generale.annulla")}
          </button>
          <button type="button" className="pulsante pulsante-primario" onClick={() => vai(ospite ? PERCORSO_ACCESSO : PERCORSO_PIANI)}>
            {t(ospite ? "account.accedi" : "gruppo.passa_gold")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="crea-sfida"
      onSubmit={(e) => {
        e.preventDefault();
        const n = nome.trim();
        if (n) crea(n);
      }}
    >
      <p className="nota">{t("gruppo.come_funziona")}</p>
      <CampoNome valore={nome} onCambia={setNome} />
      {errore && (
        <p className="admin-errore" role="alert">
          {t(`gruppo.errore_${errore}`)}
        </p>
      )}
      <div className="azioni-riga">
        <button type="button" className="pulsante pulsante-secondario" onClick={onAnnulla}>
          {t("generale.annulla")}
        </button>
        <button type="submit" className="pulsante pulsante-primario" disabled={invio}>
          {invio ? t("gioco.creazione") : t("gruppo.crea")}
        </button>
      </div>
    </form>
  );
}
